import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Users, Calendar, DollarSign, CheckCircle, XCircle, Clock, ArrowLeft, Search, Printer, CreditCard, Gift, Shield, Pencil, HandCoins } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { useChapters } from "@/hooks/useChapters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;



// Meeting types based on chapter
const MEETING_TYPES_BY_CHAPTER = {
  National: [
    { value: 'national_board', label: 'National Board' },
    { value: 'national_quarterly', label: 'National Quarterly' },
    { value: 'budget_committee', label: 'Budget Committee' }
  ],
  AD: [
    { value: 'national_quarterly', label: 'National Quarterly' },
    { value: 'joint_officers', label: 'Joint Officers' },
    { value: 'chapter_officers', label: 'Chapter Officers' },
    { value: 'ad_chapter', label: 'AD Chapter' }
  ],
  HA: [
    { value: 'national_quarterly', label: 'National Quarterly' },
    { value: 'joint_officers', label: 'Joint Officers' },
    { value: 'chapter_officers', label: 'Chapter Officers' },
    { value: 'ha_chapter', label: 'HA Chapter' }
  ],
  HS: [
    { value: 'national_quarterly', label: 'National Quarterly' },
    { value: 'joint_officers', label: 'Joint Officers' },
    { value: 'chapter_officers', label: 'Chapter Officers' },
    { value: 'hs_chapter', label: 'HS Chapter' }
  ]
};

// Dues status options with colors
const DUES_STATUSES = [
  { value: 'paid', label: 'Paid', color: 'bg-green-600', icon: CheckCircle },
  { value: 'forgiven', label: 'Forgiven', color: 'bg-purple-600', icon: Gift },
  { value: 'extended', label: 'Extended', color: 'bg-blue-600', icon: Shield },
  { value: 'late', label: 'Late', color: 'bg-orange-500', icon: Clock },
  { value: 'unpaid', label: 'Not Paid', color: 'bg-red-600', icon: XCircle }
];

function OfficerTracking() {
  const { active: activeChapters, inactive: inactiveChapters } = useChapters();
  const CHAPTERS = [...activeChapters, ...inactiveChapters];
  const navigate = useNavigate();
  const [members, setMembers] = useState({});
  const [attendance, setAttendance] = useState([]);
  const [dues, setDues] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedChapter, setSelectedChapter] = useState('National');
  const [activeTab, setActiveTab] = useState('attendance');
  const [canEdit, setCanEdit] = useState(false);
  const [canEditDues, setCanEditDues] = useState(false);
  const [canViewFinancialAssistance, setCanViewFinancialAssistance] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Square Payments State
  const [unmatchedPayments, setUnmatchedPayments] = useState([]);
  const [showPaymentsDialog, setShowPaymentsDialog] = useState(false);
  const [matchingPayment, setMatchingPayment] = useState(null);
  const [matchMemberId, setMatchMemberId] = useState('');
  const [matchYear, setMatchYear] = useState(new Date().getFullYear());
  const [matchMonth, setMatchMonth] = useState(new Date().getMonth());
  
  // View Member Meetings Dialog
  const [viewMeetingsDialog, setViewMeetingsDialog] = useState(false);
  const [viewMeetingsMember, setViewMeetingsMember] = useState(null);
  const [viewMeetingsFilter, setViewMeetingsFilter] = useState('all'); // 'all' or 'YYYY-MM'
  const printRef = useRef(null);
  
  // Dues History Dialog
  const [duesHistoryDialog, setDuesHistoryDialog] = useState(false);
  const [duesHistoryMember, setDuesHistoryMember] = useState(null);
  const [duesHistoryData, setDuesHistoryData] = useState(null);
  const [loadingDuesHistory, setLoadingDuesHistory] = useState(false);
  
  // Dues Extensions
  const [allExtensions, setAllExtensions] = useState([]);
  
  // Attendance Dialog
  const [attendanceDialog, setAttendanceDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [attendanceForm, setAttendanceForm] = useState({
    meeting_date: new Date().toISOString().split('T')[0],
    meeting_type: 'national_officer',
    status: 'present',
    notes: ''
  });
  
  // Dues Dialog - Simplified
  const [duesDialog, setDuesDialog] = useState(false);
  const [duesForm, setDuesForm] = useState({
    status: 'paid',
    notes: '',
    extensionDate: ''
  });
  const [memberExtension, setMemberExtension] = useState(null);
  
  // Payment Dialog - for recording payments with amount
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'cash',
    notes: ''
  });
  const [memberBalance, setMemberBalance] = useState(0);
  const [paymentLoading, setPaymentLoading] = useState(false);
  
  // Bulk Dues Correction Dialog
  const [bulkCorrectionDialog, setBulkCorrectionDialog] = useState(false);
  const [bulkCorrectionForm, setBulkCorrectionForm] = useState({
    startMonth: '',
    startYear: new Date().getFullYear().toString(),
    endMonth: '',
    endYear: new Date().getFullYear().toString(),
    status: 'paid',
    note: '',
    revokeExtension: true
  });
  
  const token = localStorage.getItem('token');
  const userTitle = localStorage.getItem('title');
  const userRole = localStorage.getItem('role');
  
  // Check if user can edit attendance - based on edit_attendance permission
  // Check if user can edit dues - based on edit_dues permission
  // Also listen for permission changes via storage event and window focus
  useEffect(() => {
    const updatePermissions = () => {
      const permissions = JSON.parse(localStorage.getItem('permissions') || '{}');
      const userChapter = localStorage.getItem('chapter');
      const canUserEditAttendance = permissions.edit_attendance === true;
      const canUserEditDues = permissions.edit_dues === true;
      // Financial Assistance ledger is National-only
      const canFA =
        userChapter === 'National' &&
        (permissions.view_financial_assistance === true ||
          permissions.manage_financial_assistance === true);
      setCanEdit(canUserEditAttendance);
      setCanEditDues(canUserEditDues);
      setCanViewFinancialAssistance(canFA);
    };
    
    // Initial check
    updatePermissions();
    
    // Listen for storage changes (from other tabs or App.js refresh)
    const handleStorage = (e) => {
      if (e.key === 'permissions') {
        updatePermissions();
      }
    };
    
    // Also refresh on window focus
    const handleFocus = () => {
      updatePermissions();
    };
    
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleFocus);
    
    // Check every 30 seconds for permission updates
    const intervalId = setInterval(updatePermissions, 30000);
    
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleFocus);
      clearInterval(intervalId);
    };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [membersRes, attendanceRes, duesRes, summaryRes, extensionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/officer-tracking/members`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/officer-tracking/attendance`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/officer-tracking/dues`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/officer-tracking/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/dues-reminders/extensions`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { extensions: [] } }))
      ]);
      
      setMembers(membersRes.data);
      setAttendance(attendanceRes.data);
      setDues(duesRes.data);
      setSummary(summaryRes.data);
      setAllExtensions(extensionsRes.data?.extensions || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Access denied. Officers only.");
        navigate('/');
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getAttendanceForMember = (memberId) => {
    return attendance.filter(a => a.member_id === memberId);
  };

  const getDuesForMember = (memberId) => {
    return dues.filter(d => d.member_id === memberId);
  };

  // Check if member has an active extension
  const getMemberExtension = (memberId) => {
    return allExtensions.find(e => e.member_id === memberId && e.is_active);
  };

  const getAttendanceStats = (memberId) => {
    const records = getAttendanceForMember(memberId);
    const present = records.filter(r => r.status === 'present').length;
    const total = records.length;
    return { present, total, rate: total > 0 ? Math.round(present / total * 100) : 0 };
  };

  // Get current month dues status for a member
  const getCurrentMonthDues = (memberId) => {
    const now = new Date();
    const currentMonth = `${now.toLocaleString('en-US', { month: 'short' })}_${now.getFullYear()}`;
    return dues.find(d => d.member_id === memberId && d.month === currentMonth);
  };

  const openAttendanceDialog = (member) => {
    setSelectedMember(member);
    // Get the first meeting type for the selected chapter as default
    const defaultMeetingType = MEETING_TYPES_BY_CHAPTER[selectedChapter]?.[0]?.value || 'national_board';
    setAttendanceForm({
      meeting_date: new Date().toISOString().split('T')[0],
      meeting_type: defaultMeetingType,
      status: 'present',
      notes: ''
    });
    setAttendanceDialog(true);
  };

  const openViewMeetingsDialog = (member) => {
    setViewMeetingsMember(member);
    setViewMeetingsFilter('all');
    setViewMeetingsDialog(true);
  };

  // Open dues history dialog for a member
  const openDuesHistoryDialog = async (member) => {
    setDuesHistoryMember(member);
    setDuesHistoryDialog(true);
    setLoadingDuesHistory(true);
    setDuesHistoryData(null);
    
    try {
      // Fetch both history and balance in parallel
      const [historyResponse, balanceResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/officer-tracking/dues/history/${member.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/dues/balance/${member.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { balance: 0 } }))
      ]);
      
      setDuesHistoryData({
        ...historyResponse.data,
        balance: balanceResponse.data.balance || 0
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load dues history");
    } finally {
      setLoadingDuesHistory(false);
    }
  };

  // Get meeting type label from value
  const getMeetingTypeLabel = (value) => {
    const allMeetingTypes = [
      ...MEETING_TYPES_BY_CHAPTER.National,
      ...MEETING_TYPES_BY_CHAPTER.AD,
      ...MEETING_TYPES_BY_CHAPTER.HA,
      ...MEETING_TYPES_BY_CHAPTER.HS
    ];
    const found = allMeetingTypes.find(t => t.value === value);
    return found ? found.label : value;
  };

  // Get filtered meetings for view dialog
  const getFilteredMeetings = (memberId) => {
    const allMeetings = getAttendanceForMember(memberId);
    if (viewMeetingsFilter === 'all') {
      return allMeetings.sort((a, b) => new Date(b.meeting_date) - new Date(a.meeting_date));
    }
    // Filter by month (YYYY-MM format)
    return allMeetings
      .filter(m => m.meeting_date && m.meeting_date.startsWith(viewMeetingsFilter))
      .sort((a, b) => new Date(b.meeting_date) - new Date(a.meeting_date));
  };

  // Get unique months from attendance records
  const getAvailableMonths = (memberId) => {
    const meetings = getAttendanceForMember(memberId);
    const months = new Set();
    meetings.forEach(m => {
      if (m.meeting_date) {
        const yearMonth = m.meeting_date.substring(0, 7); // YYYY-MM
        months.add(yearMonth);
      }
    });
    return Array.from(months).sort().reverse();
  };

  // Format month for display
  const formatMonth = (yearMonth) => {
    const [year, month] = yearMonth.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  // Print meetings
  const handlePrintMeetings = () => {
    const content = printRef.current;
    if (!content) return;
    
    const printWindow = window.open('', '_blank');
    const meetings = getFilteredMeetings(viewMeetingsMember?.id || '');
    const stats = getAttendanceStats(viewMeetingsMember?.id || '');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Meeting Attendance - ${viewMeetingsMember?.handle}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { font-size: 18px; margin-bottom: 5px; }
            h2 { font-size: 14px; color: #666; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
            .stats { margin-bottom: 15px; padding: 10px; background: #f9f9f9; border-radius: 5px; }
            .present { color: green; }
            .absent { color: red; }
            .excused { color: orange; }
          </style>
        </head>
        <body>
          <h1>Meeting Attendance Report</h1>
          <h2>${viewMeetingsMember?.handle} (${viewMeetingsMember?.title || 'Member'}) - ${viewMeetingsFilter === 'all' ? 'All Time' : formatMonth(viewMeetingsFilter)}</h2>
          <div class="stats">
            <strong>Attendance Rate:</strong> ${stats.rate}% (${stats.present} present out of ${stats.total} meetings)
          </div>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Meeting Type</th>
                <th>Status</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${meetings.map(m => `
                <tr>
                  <td>${m.meeting_date}</td>
                  <td>${getMeetingTypeLabel(m.meeting_type)}</td>
                  <td class="${m.status}">${m.status.charAt(0).toUpperCase() + m.status.slice(1)}</td>
                  <td>${m.notes || '-'}</td>
                </tr>
              `).join('')}
              ${meetings.length === 0 ? '<tr><td colspan="4" style="text-align:center;">No meetings found</td></tr>' : ''}
            </tbody>
          </table>
          <p style="margin-top: 20px; font-size: 12px; color: #999;">Printed on ${new Date().toLocaleDateString()}</p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Fetch unmatched Square payments
  const fetchUnmatchedPayments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dues/unmatched-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnmatchedPayments(response.data || []);
    } catch (error) {
      console.error("Failed to fetch unmatched payments:", error);
    }
  };

  // Sync subscriptions from Square
  const handleSyncSubscriptions = async () => {
    try {
      toast.info("Syncing subscriptions from Square payment history...");
      const response = await axios.post(`${BACKEND_URL}/api/dues/sync-subscriptions`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      toast.success(`Synced ${data.members_synced} members, marked ${data.months_marked_paid} months as paid`);
      fetchData(); // Refresh dues data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync subscriptions");
    }
  };

  // Sync payment links from Square (one-time dues payments)
  const handleSyncPaymentLinks = async () => {
    try {
      toast.info("Syncing payment links from Square...");
      const response = await axios.post(`${BACKEND_URL}/api/dues/sync-payment-links`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      toast.success(`Synced ${data.orders_synced} payments, marked ${data.months_marked_paid} months as paid`);
      fetchData(); // Refresh dues data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync payment links");
    }
  };

  // Sync all dues (subscriptions + payment links)
  const handleSyncAllDues = async () => {
    try {
      toast.info("Syncing all dues from Square...");
      
      // Run both syncs
      const [subsResponse, linksResponse] = await Promise.all([
        axios.post(`${BACKEND_URL}/api/dues/sync-subscriptions`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.post(`${BACKEND_URL}/api/dues/sync-payment-links`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const totalMembers = (subsResponse.data.members_synced || 0) + (linksResponse.data.orders_synced || 0);
      const totalMonths = (subsResponse.data.months_marked_paid || 0) + (linksResponse.data.months_marked_paid || 0);
      
      toast.success(`Synced ${totalMembers} payments, marked ${totalMonths} months as paid`);
      fetchData(); // Refresh dues data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync dues");
    }
  };

  // View subscriptions
  const [subscriptions, setSubscriptions] = useState({ matched: [], unmatched: [] });
  const [showSubscriptionsDialog, setShowSubscriptionsDialog] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  
  // Manual linking state
  const [allMembersForLinking, setAllMembersForLinking] = useState([]);
  const [linkingSubscription, setLinkingSubscription] = useState(null);
  const [selectedMemberForLink, setSelectedMemberForLink] = useState('');
  const [linkingInProgress, setLinkingInProgress] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(null);

  const handleViewSubscriptions = async () => {
    setLoadingSubscriptions(true);
    setShowSubscriptionsDialog(true);
    try {
      const [subsResponse, membersResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dues/subscriptions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/dues/all-members-for-linking`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSubscriptions(subsResponse.data);
      setAllMembersForLinking(membersResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch subscriptions");
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const handleCancelSquareSubscription = async (subscription) => {
    if (!window.confirm(`⚠️ CANCEL SUBSCRIPTION\n\nAre you sure you want to cancel the Square subscription for:\n\n${subscription.customer_name || 'Unknown'} (${subscription.customer_email || 'No email'})\n\nThis will cancel their recurring payment in Square.`)) {
      return;
    }
    
    setCancellingSubscription(subscription.subscription_id);
    try {
      await axios.delete(`${BACKEND_URL}/api/dues/cancel-square-subscription/${subscription.subscription_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Square subscription cancelled successfully!");
      
      // Refresh subscriptions
      const response = await axios.get(`${BACKEND_URL}/api/dues/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptions(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancellingSubscription(null);
    }
  };

  const handleLinkSubscription = async () => {
    if (!linkingSubscription || !selectedMemberForLink) {
      toast.error("Please select a member");
      return;
    }
    
    setLinkingInProgress(true);
    try {
      await axios.post(`${BACKEND_URL}/api/dues/link-subscription`, {
        member_id: selectedMemberForLink,
        square_customer_id: linkingSubscription.customer_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Subscription linked successfully!");
      setLinkingSubscription(null);
      setSelectedMemberForLink('');
      
      // Refresh subscriptions
      const response = await axios.get(`${BACKEND_URL}/api/dues/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptions(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link subscription");
    } finally {
      setLinkingInProgress(false);
    }
  };

  const getMatchTypeLabel = (matchType, score) => {
    if (!matchType) return null;
    const labels = {
      'manual_link': { text: 'Manual', color: 'bg-blue-600' },
      'exact_name': { text: 'Exact', color: 'bg-green-600' },
      'exact_handle': { text: 'Exact', color: 'bg-green-600' },
      'fuzzy_name': { text: `Fuzzy ${score}%`, color: score >= 90 ? 'bg-green-500' : 'bg-yellow-500' },
      'fuzzy_handle': { text: `Fuzzy ${score}%`, color: score >= 90 ? 'bg-green-500' : 'bg-yellow-500' },
      'partial_name': { text: 'Partial', color: 'bg-yellow-600' }
    };
    return labels[matchType] || { text: matchType, color: 'bg-gray-500' };
  };

  const openDuesDialog = async (member, preselectedStatus = null) => {
    setSelectedMember(member);
    const existing = getCurrentMonthDues(member.id);
    
    // Fetch member's current extension if any
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dues-reminders/extensions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const extensions = response.data.extensions || [];
      const memberExt = extensions.find(e => e.member_id === member.id && e.is_active);
      setMemberExtension(memberExt || null);
      
      // If member has active extension, pre-fill the date
      if (memberExt) {
        const extDate = memberExt.extension_until?.split('T')[0] || '';
        setDuesForm({
          status: preselectedStatus || existing?.status || 'extended',
          notes: existing?.notes || '',
          extensionDate: extDate,
          hasArrangement: member.dues_arrangements_made || false
        });
      } else {
        setDuesForm({
          status: preselectedStatus || existing?.status || 'paid',
          notes: existing?.notes || '',
          extensionDate: '',
          hasArrangement: member.dues_arrangements_made || false
        });
      }
    } catch (error) {
      console.error("Failed to fetch extensions:", error);
      setMemberExtension(null);
      setDuesForm({
        status: preselectedStatus || existing?.status || 'paid',
        notes: existing?.notes || '',
        extensionDate: '',
        hasArrangement: member.dues_arrangements_made || false
      });
    }
    
    setDuesDialog(true);
  };

  // Open payment dialog to record a payment with amount
  const openPaymentDialog = async (member) => {
    setSelectedMember(member);
    setPaymentForm({ amount: '', payment_method: 'cash', notes: '' });
    
    // Fetch member's current balance
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dues/balance/${member.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMemberBalance(response.data.balance || 0);
    } catch (error) {
      console.error("Failed to fetch balance:", error);
      setMemberBalance(0);
    }
    
    setPaymentDialog(true);
  };

  // Handle payment submission
  const handlePaymentSubmit = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }
    
    setPaymentLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/dues/payment`, {
        member_id: selectedMember.id,
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      const result = response.data;
      const monthsPaid = result.months_paid?.length || 0;
      const balance = result.remaining_balance || 0;
      
      let message = `Payment of $${paymentForm.amount} recorded.`;
      if (monthsPaid > 0) {
        message += ` ${monthsPaid} month(s) marked as paid.`;
      }
      if (balance > 0) {
        message += ` Credit balance: $${balance}`;
      }
      
      toast.success(message);
      setPaymentDialog(false);
      fetchData();
    } catch (error) {
      console.error("Failed to record payment:", error);
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleAttendanceSubmit = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/officer-tracking/attendance`, {
        member_id: selectedMember.id,
        ...attendanceForm
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success("Attendance recorded");
      setAttendanceDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record attendance");
    }
  };

  const handleDuesSubmit = async () => {
    try {
      const now = new Date();
      const currentMonth = `${now.toLocaleString('en-US', { month: 'short' })}_${now.getFullYear()}`;
      
      // If status is 'extended', create/update extension
      if (duesForm.status === 'extended') {
        if (!duesForm.extensionDate) {
          toast.error("Please enter an extension date");
          return;
        }
        
        // Create or update extension
        await axios.post(`${BACKEND_URL}/api/dues-reminders/extensions`, {
          member_id: selectedMember.id,
          extension_until: duesForm.extensionDate,
          reason: duesForm.notes || 'Extension granted from A&D panel'
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        toast.success("Extension granted");
      } else {
        // If status is 'paid' and member had an extension, revoke it
        if (duesForm.status === 'paid' && memberExtension) {
          try {
            await axios.delete(`${BACKEND_URL}/api/dues-reminders/extensions/${selectedMember.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (err) {
            // Extension might already be removed, continue
            console.log("Extension revoke:", err.response?.data?.detail || err.message);
          }
        }
      }
      
      // Update dues status
      await axios.post(`${BACKEND_URL}/api/officer-tracking/dues`, {
        member_id: selectedMember.id,
        month: currentMonth,
        status: duesForm.status,
        notes: duesForm.notes
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success("Dues status updated");
      setDuesDialog(false);
      setMemberExtension(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update dues");
    }
  };

  // Open bulk correction dialog for a member
  const openBulkCorrectionDialog = (member) => {
    setSelectedMember(member);
    const now = new Date();
    const currentMonth = now.toLocaleString('en-US', { month: 'short' });
    const currentYear = now.getFullYear().toString();
    
    setBulkCorrectionForm({
      startMonth: currentMonth,
      startYear: currentYear,
      endMonth: currentMonth,
      endYear: currentYear,
      status: 'paid',
      note: '',
      revokeExtension: true
    });
    setBulkCorrectionDialog(true);
  };

  // Handle bulk dues correction submission
  const handleBulkCorrectionSubmit = async () => {
    try {
      const startMonthKey = `${bulkCorrectionForm.startMonth}_${bulkCorrectionForm.startYear}`;
      const endMonthKey = `${bulkCorrectionForm.endMonth}_${bulkCorrectionForm.endYear}`;
      
      const response = await axios.post(`${BACKEND_URL}/api/dues-reminders/bulk-correction`, {
        member_id: selectedMember.id,
        start_month: startMonthKey,
        end_month: endMonthKey,
        status: bulkCorrectionForm.status,
        note: bulkCorrectionForm.note,
        revoke_extension: bulkCorrectionForm.revokeExtension
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(response.data.message || "Dues corrected successfully");
      setBulkCorrectionDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to correct dues");
    }
  };

  // Delete attendance record (syncs to member's meeting_attendance)
  const handleDeleteAttendance = async (recordId) => {
    if (!confirm("Are you sure you want to delete this attendance record?")) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/officer-tracking/attendance/${recordId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Attendance record deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete attendance");
    }
  };

  // Quick dues update without dialog
  const handleQuickDuesUpdate = async (member, status) => {
    try {
      const now = new Date();
      const currentMonth = `${now.toLocaleString('en-US', { month: 'short' })}_${now.getFullYear()}`;
      
      await axios.post(`${BACKEND_URL}/api/officer-tracking/dues`, {
        member_id: member.id,
        month: currentMonth,
        status: status,
        notes: ''
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Dues marked as ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update dues");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      present: <Badge className="bg-green-500 text-black"><CheckCircle className="w-3 h-3 mr-1" />Present</Badge>,
      absent: <Badge className="bg-red-500 text-black"><XCircle className="w-3 h-3 mr-1" />Absent</Badge>,
      excused: <Badge className="bg-yellow-400 text-black"><Clock className="w-3 h-3 mr-1" />Excused</Badge>,
      paid: <Badge className="bg-green-600"><CheckCircle className="w-3 h-3 mr-1" />Paid</Badge>,
      unpaid: <Badge className="bg-red-600"><XCircle className="w-3 h-3 mr-1" />Not Paid</Badge>,
      late: <Badge className="bg-orange-500"><Clock className="w-3 h-3 mr-1" />Late</Badge>,
      forgiven: <Badge className="bg-purple-600"><Gift className="w-3 h-3 mr-1" />Forgiven</Badge>,
      extended: <Badge className="bg-blue-600"><Shield className="w-3 h-3 mr-1" />Extended</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  // Filter members by search term
  const filteredMembers = (members[selectedChapter] || []).filter(member => 
    member.handle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 sticky top-0 z-10">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 max-w-7xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="h-8 w-8 sm:h-10 sm:w-10">
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2 text-white">
                  <Users className="h-5 w-5 sm:h-6 sm:w-6" />
                  A & D
                </h1>
                <p className="text-muted-foreground text-xs sm:text-sm hidden sm:block">
                  Attendance & Dues Tracking
                  {!canEdit && <span className="text-yellow-500 ml-2">(View Only)</span>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canViewFinancialAssistance && (
                <Button
                  onClick={() => navigate('/financial-assistance')}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs sm:text-sm h-8 sm:h-9"
                  data-testid="open-financial-assistance-btn"
                >
                  <HandCoins className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden xs:inline">Financial Assistance</span>
                  <span className="xs:hidden">Assistance</span>
                </Button>
              )}
              {!canEdit && <Badge variant="outline" className="text-yellow-500 border-yellow-500 text-xs sm:hidden">View Only</Badge>}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-7xl">
        {/* Summary Cards - Responsive Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
          {CHAPTERS.map(chapter => (
            <Card key={chapter} 
              className={`cursor-pointer transition-all bg-slate-800 border-slate-700 ${selectedChapter === chapter ? 'ring-2 ring-primary' : ''} ${inactiveChapters.includes(chapter) ? 'opacity-60' : ''}`}
              onClick={() => setSelectedChapter(chapter)}
            >
              <CardHeader className="p-3 sm:p-4 pb-1 sm:pb-2">
                <CardTitle className="text-sm sm:text-lg text-white">{chapter}{inactiveChapters.includes(chapter) ? <span className="text-slate-500 text-xs font-normal ml-1">(inactive)</span> : null}</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0">
                <div className="text-xl sm:text-2xl font-bold text-white">{members[chapter]?.length || 0}</div>
                <div className="text-xs text-muted-foreground">Members</div>
                {summary[chapter] && (
                  <div className="mt-1 sm:mt-2 text-xs space-y-0.5 sm:space-y-1">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Attend:</span>
                      <span className={summary[chapter].attendance_rate >= 80 ? 'text-green-500' : 'text-yellow-500'}>
                        {summary[chapter].attendance_rate}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Dues:</span>
                      <span className="text-white">{summary[chapter].dues_paid}/{summary[chapter].dues_total}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content Card */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="p-3 sm:p-6">
            <div className="flex flex-col gap-3 sm:gap-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4">
                <div>
                  <CardTitle className="text-base sm:text-xl text-white">{selectedChapter} Chapter</CardTitle>
                  <CardDescription className="text-xs sm:text-sm hidden sm:block">Track attendance and dues for {selectedChapter} members</CardDescription>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 h-9 sm:h-10 bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              
              {/* View Toggle Buttons - Responsive */}
              <div className="flex gap-2 sm:gap-4">
                <Button
                  onClick={() => setActiveTab('attendance')}
                  className={`flex-1 h-10 sm:h-14 text-sm sm:text-lg font-bold ${
                    activeTab === 'attendance' 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                      : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                  }`}
                >
                  <Calendar className="w-4 h-4 sm:w-6 sm:h-6 mr-1 sm:mr-3" />
                  <span className="hidden xs:inline">Attendance</span>
                  <span className="xs:hidden">Attend</span>
                </Button>
                <Button
                  onClick={() => setActiveTab('dues')}
                  className={`flex-1 h-10 sm:h-14 text-sm sm:text-lg font-bold ${
                    activeTab === 'dues' 
                      ? 'bg-green-600 hover:bg-green-700 text-white' 
                      : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                  }`}
                >
                  <DollarSign className="w-4 h-4 sm:w-6 sm:h-6 mr-1 sm:mr-3" />
                  Dues
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-3 sm:p-6 pt-0">
            {/* Attendance Content */}
            {activeTab === 'attendance' && (
              <div>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Member</TableHead>
                        <TableHead className="text-slate-400">Title</TableHead>
                        <TableHead className="text-slate-400">Attendance Rate</TableHead>
                        <TableHead className="text-slate-400">Last Meeting</TableHead>
                        <TableHead className="text-slate-400">View</TableHead>
                        {canEdit && <TableHead className="text-slate-400">Action</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMembers.map(member => {
                        const stats = getAttendanceStats(member.id);
                        const lastRecord = getAttendanceForMember(member.id).sort((a, b) => 
                          new Date(b.meeting_date) - new Date(a.meeting_date)
                        )[0];
                        
                        return (
                          <TableRow key={member.id} className="border-slate-700">
                            <TableCell className="font-medium text-white">{member.handle}</TableCell>
                            <TableCell className="text-slate-300">{member.title || '-'}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className={stats.rate >= 80 ? 'text-green-500' : stats.rate >= 50 ? 'text-yellow-500' : 'text-red-500'}>
                                  {stats.rate}%
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  ({stats.present}/{stats.total})
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {lastRecord ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-slate-300">{lastRecord.meeting_date}</span>
                                  {getStatusBadge(lastRecord.status)}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-xs">No records</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Button size="sm" variant="outline" onClick={() => openViewMeetingsDialog(member)} className="text-xs border-slate-500 bg-slate-700 hover:bg-slate-600 text-white">
                                View All
                              </Button>
                            </TableCell>
                            {canEdit && (
                              <TableCell>
                                <Button size="sm" onClick={() => openAttendanceDialog(member)} className="text-xs bg-blue-600 hover:bg-blue-700 text-white">
                                  Record
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile Card View for Attendance */}
                <div className="md:hidden space-y-3">
                  {filteredMembers.map(member => {
                    const stats = getAttendanceStats(member.id);
                    const lastRecord = getAttendanceForMember(member.id).sort((a, b) => 
                      new Date(b.meeting_date) - new Date(a.meeting_date)
                    )[0];
                    
                    return (
                      <div key={member.id} className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="font-medium text-white">{member.handle}</div>
                            <div className="text-xs text-slate-400">{member.title || 'Member'}</div>
                          </div>
                          <div className="text-right">
                            <div className={`text-lg font-bold ${stats.rate >= 80 ? 'text-green-500' : stats.rate >= 50 ? 'text-yellow-500' : 'text-red-500'}`}>
                              {stats.rate}%
                            </div>
                            <div className="text-xs text-slate-400">({stats.present}/{stats.total})</div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
                          <span>Last:</span>
                          {lastRecord ? (
                            <div className="flex items-center gap-1">
                              <span>{lastRecord.meeting_date}</span>
                              {getStatusBadge(lastRecord.status)}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">No records</span>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openViewMeetingsDialog(member)} className="flex-1 text-xs h-8 border-slate-500 bg-slate-700 hover:bg-slate-600 text-white">
                            View All
                          </Button>
                          {canEdit && (
                            <Button size="sm" onClick={() => openAttendanceDialog(member)} className="flex-1 text-xs h-8 bg-blue-600 hover:bg-blue-700 text-white">
                              Record
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Dues Content */}
            {activeTab === 'dues' && (
              <div className="space-y-4">
                {/* Square Sync Buttons - Responsive */}
                {canEditDues && (
                  <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                    <Button variant="outline" onClick={handleViewSubscriptions} className="text-xs sm:text-sm h-9 sm:h-10 border-slate-500 bg-slate-700 hover:bg-slate-600 text-white">
                      <CreditCard className="w-4 h-4 mr-2" />
                      View Subscriptions
                    </Button>
                    <Button onClick={handleSyncAllDues} className="bg-purple-600 hover:bg-purple-700 text-xs sm:text-sm h-9 sm:h-10">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Sync from Square
                    </Button>
                  </div>
                )}
                
                {/* Desktop Table View for Dues */}
                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Member</TableHead>
                        <TableHead className="text-slate-400">Title</TableHead>
                        <TableHead className="text-slate-400">Current Month Status</TableHead>
                        <TableHead className="text-slate-400">Balance</TableHead>
                        <TableHead className="text-slate-400">History</TableHead>
                        {canEditDues && <TableHead className="text-slate-400">Quick Update</TableHead>}
                        {canEditDues && <TableHead className="text-slate-400">Notes</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMembers.map(member => {
                        const currentDues = getCurrentMonthDues(member.id);
                        const isExempt = member.non_dues_paying;
                        const isSuspended = member.dues_suspended;
                        const extension = getMemberExtension(member.id);
                        const hasBalance = member.dues_balance > 0;
                        const hasArrangements = member.dues_arrangements_made;
                        
                        return (
                          <TableRow key={member.id} className={`border-slate-700 ${isSuspended ? 'bg-red-900/20' : ''} ${hasArrangements ? 'bg-green-900/10' : ''}`}>
                            <TableCell 
                              className="font-medium text-blue-400 hover:text-blue-300 cursor-pointer underline"
                              onClick={() => openDuesHistoryDialog(member)}
                            >
                              {member.handle}
                              {hasArrangements && (
                                <Badge className="ml-2 bg-green-600 text-white text-xs animate-pulse">
                                  ✓ ARRANGEMENT
                                </Badge>
                              )}
                              {isSuspended && !hasArrangements && (
                                <Badge className="ml-2 bg-red-600 text-white text-xs">Suspended</Badge>
                              )}
                              {isExempt && (
                                <Badge className="ml-2 bg-amber-600 text-white text-xs">Exempt</Badge>
                              )}
                              {extension && (
                                <Badge className="ml-2 bg-blue-600 text-white text-xs" title={`Until ${new Date(extension.extension_until).toLocaleDateString()}`}>
                                  Extended
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-slate-300">{member.title || '-'}</TableCell>
                            <TableCell>
                              <div 
                                className={canEditDues ? "cursor-pointer hover:opacity-80" : ""}
                                onClick={() => canEditDues && openDuesDialog(member)}
                                title={canEditDues ? "Click to edit" : ""}
                              >
                                {isSuspended && !hasArrangements ? (
                                  <Badge className="bg-red-600">Suspended</Badge>
                                ) : hasArrangements ? (
                                  <Badge className="bg-green-600">✓ Arrangement</Badge>
                                ) : isExempt ? (
                                  <Badge className="bg-amber-600">Non-Dues Paying</Badge>
                                ) : extension ? (
                                  <Badge className="bg-blue-600">Extended to {new Date(extension.extension_until).toLocaleDateString()}</Badge>
                                ) : currentDues ? getStatusBadge(currentDues.status) : (
                                  <Badge variant="outline">Not Recorded</Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {hasBalance ? (
                                <span className="text-green-400 font-mono text-sm">
                                  ${member.dues_balance.toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-slate-500 text-sm">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openDuesHistoryDialog(member)}
                                className="text-xs h-7"
                              >
                                View
                              </Button>
                            </TableCell>
                            {canEditDues && (
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700 h-7 px-2 text-xs"
                                    onClick={() => handleQuickDuesUpdate(member, 'paid')}
                                  >
                                    <CheckCircle className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-orange-500 hover:bg-orange-600 h-7 px-2 text-xs"
                                    onClick={() => handleQuickDuesUpdate(member, 'late')}
                                  >
                                    <Clock className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-red-600 hover:bg-red-700 h-7 px-2 text-xs"
                                    onClick={() => handleQuickDuesUpdate(member, 'unpaid')}
                                  >
                                    <XCircle className="w-3 h-3" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                            {canEditDues && (
                              <TableCell>
                                <Button
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7"
                                  onClick={() => openDuesDialog(member)}
                                >
                                  Edit
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile Card View for Dues */}
                <div className="md:hidden space-y-3">
                  {filteredMembers.map(member => {
                    const currentDues = getCurrentMonthDues(member.id);
                    const isExempt = member.non_dues_paying;
                    const isSuspended = member.dues_suspended;
                    const extension = getMemberExtension(member.id);
                    
                    return (
                      <div key={member.id} className={`rounded-lg p-3 border ${isSuspended ? 'bg-red-900/30 border-red-700' : 'bg-slate-700/50 border-slate-600'}`}>
                        <div 
                          className="flex justify-between items-start mb-3 cursor-pointer"
                          onClick={() => openDuesHistoryDialog(member)}
                        >
                          <div>
                            <div className="font-medium text-blue-400 underline">
                              {member.handle}
                              {isSuspended && (
                                <Badge className="ml-2 bg-red-600 text-white text-xs">Suspended</Badge>
                              )}
                              {isExempt && (
                                <Badge className="ml-2 bg-amber-600 text-white text-xs">Exempt</Badge>
                              )}
                              {extension && (
                                <Badge className="ml-2 bg-blue-600 text-white text-xs">Extended</Badge>
                              )}
                            </div>
                            <div className="text-xs text-slate-400">{member.title || 'Brother'}</div>
                          </div>
                          <div>
                            {isSuspended ? (
                              <Badge className="bg-red-600 text-xs">Suspended</Badge>
                            ) : isExempt ? (
                              <Badge className="bg-amber-600 text-xs">Non-Dues Paying</Badge>
                            ) : extension ? (
                              <Badge className="bg-blue-600 text-xs">Until {new Date(extension.extension_until).toLocaleDateString()}</Badge>
                            ) : currentDues ? getStatusBadge(currentDues.status) : (
                              <Badge variant="outline" className="text-xs">Not Recorded</Badge>
                            )}
                          </div>
                        </div>
                        
                        {canEditDues && !isExempt && !isSuspended && (
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="flex-1 bg-green-600 hover:bg-green-700 h-9 text-xs"
                                onClick={() => handleQuickDuesUpdate(member, 'paid')}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Paid
                              </Button>
                              <Button
                                size="sm"
                                className="flex-1 bg-orange-500 hover:bg-orange-600 h-9 text-xs"
                                onClick={() => handleQuickDuesUpdate(member, 'late')}
                              >
                                <Clock className="w-4 h-4 mr-1" />
                                Late
                              </Button>
                              <Button
                                size="sm"
                                className="flex-1 bg-red-600 hover:bg-red-700 h-9 text-xs"
                                onClick={() => handleQuickDuesUpdate(member, 'unpaid')}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Unpaid
                              </Button>
                            </div>
                            <Button
                              size="sm"
                              className="w-full bg-blue-600 hover:bg-blue-700 h-9 text-xs"
                              onClick={() => openDuesDialog(member)}
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Edit / Add Extension
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Subscriptions Dialog */}
      <Dialog open={showSubscriptionsDialog} onOpenChange={setShowSubscriptionsDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Square Subscriptions</DialogTitle>
            <DialogDescription>
              Active dues subscriptions from Square • {subscriptions.customer_fetch_method === 'batch' && '⚡ Using batch API'}
            </DialogDescription>
          </DialogHeader>
          {loadingSubscriptions ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-muted-foreground">Loading subscriptions...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Matched Subscriptions */}
              <div>
                <h3 className="font-semibold text-green-500 mb-2">
                  ✓ Matched Subscriptions ({subscriptions.matched?.length || 0})
                </h3>
                {subscriptions.matched?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer Name</TableHead>
                        <TableHead>Matched Member</TableHead>
                        <TableHead>Match</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Paid Through</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subscriptions.matched.map((sub, idx) => {
                        const matchInfo = getMatchTypeLabel(sub.match_type, sub.match_score);
                        return (
                          <TableRow key={idx}>
                            <TableCell>{sub.customer_name}</TableCell>
                            <TableCell className="text-green-500 font-medium">{sub.matched_member_handle}</TableCell>
                            <TableCell>
                              {matchInfo && (
                                <Badge className={`${matchInfo.color} text-xs`}>{matchInfo.text}</Badge>
                              )}
                            </TableCell>
                            <TableCell><Badge className="bg-green-600">{sub.status}</Badge></TableCell>
                            <TableCell>{sub.charged_through_date}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-muted-foreground text-sm">No matched subscriptions</p>
                )}
              </div>

              {/* Unmatched Subscriptions with Manual Linking */}
              <div>
                <h3 className="font-semibold text-yellow-500 mb-2">
                  ⚠ Unmatched Subscriptions ({subscriptions.unmatched?.length || 0})
                  {subscriptions.unmatched?.length > 0 && canEditDues && (
                    <span className="text-xs font-normal text-muted-foreground ml-2">
                      Click "Link" to manually assign a member
                    </span>
                  )}
                </h3>
                {subscriptions.unmatched?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        {canEditDues && <TableHead>Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subscriptions.unmatched.map((sub, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{sub.customer_name || 'Unknown'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{sub.customer_email || '-'}</TableCell>
                          <TableCell><Badge variant="outline">{sub.status}</Badge></TableCell>
                          {canEditDues && (
                            <TableCell>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setLinkingSubscription(sub);
                                    setSelectedMemberForLink('');
                                  }}
                                >
                                  Link
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  disabled={cancellingSubscription === sub.subscription_id}
                                  onClick={() => handleCancelSquareSubscription(sub)}
                                >
                                  {cancellingSubscription === sub.subscription_id ? 'Cancelling...' : 'Cancel'}
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-green-500 text-sm">🎉 All subscriptions matched!</p>
                )}
              </div>

              {/* Manual Linking Section */}
              {linkingSubscription && (
                <div className="border rounded-lg p-4 bg-slate-800">
                  <h4 className="font-semibold mb-3">Link Subscription to Member</h4>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-muted-foreground">Customer from Square:</Label>
                      <p className="font-medium">{linkingSubscription.customer_name} ({linkingSubscription.customer_email || 'No email'})</p>
                    </div>
                    <div>
                      <Label>Select Member to Link:</Label>
                      <Select value={selectedMemberForLink} onValueChange={setSelectedMemberForLink}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a member..." />
                        </SelectTrigger>
                        <SelectContent>
                          {allMembersForLinking.map(member => (
                            <SelectItem key={member.id} value={member.id}>
                              {member.handle} - {member.name} ({member.chapter})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleLinkSubscription} 
                        disabled={!selectedMemberForLink || linkingInProgress}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {linkingInProgress ? 'Linking...' : 'Confirm Link'}
                      </Button>
                      <Button variant="outline" onClick={() => setLinkingSubscription(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubscriptionsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attendance Dialog */}
      <Dialog open={attendanceDialog} onOpenChange={setAttendanceDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Record Attendance</DialogTitle>
            <DialogDescription>
              Recording attendance for {selectedMember?.handle}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Meeting Date</Label>
              <Input 
                type="date" 
                value={attendanceForm.meeting_date}
                onChange={(e) => setAttendanceForm({...attendanceForm, meeting_date: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Meeting Type</Label>
              <Select value={attendanceForm.meeting_type} onValueChange={(v) => setAttendanceForm({...attendanceForm, meeting_type: v})}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  {(MEETING_TYPES_BY_CHAPTER[selectedChapter] || []).map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Status</Label>
              <Select value={attendanceForm.status} onValueChange={(v) => setAttendanceForm({...attendanceForm, status: v})}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="present">Present</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="excused">Excused</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Notes (Optional)</Label>
              <Textarea 
                value={attendanceForm.notes}
                onChange={(e) => setAttendanceForm({...attendanceForm, notes: e.target.value})}
                placeholder="Any additional notes..."
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setAttendanceDialog(false)} className="w-full sm:w-auto border-slate-500 bg-slate-700 hover:bg-slate-600 text-white">Cancel</Button>
            <Button onClick={handleAttendanceSubmit} className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white">Save Attendance</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dues Dialog - Simplified */}
      <Dialog open={duesDialog} onOpenChange={setDuesDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-[95vw] sm:max-w-md mx-2 sm:mx-auto p-4 sm:p-6">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-white text-lg">Update Dues</DialogTitle>
            <DialogDescription className="text-sm">
              Update dues status for {selectedMember?.handle}
              {memberExtension && (
                <span className="block mt-1 text-blue-400">
                  Current extension until: {new Date(memberExtension.extension_until).toLocaleDateString()}
                </span>
              )}
              {selectedMember?.dues_suspended && (
                <span className="block mt-1 text-red-400 font-semibold">
                  ⚠️ Member is currently SUSPENDED
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Suspension Controls */}
            <div className={`p-3 rounded-lg border ${selectedMember?.dues_suspended ? 'bg-red-900/30 border-red-700' : 'bg-slate-700/30 border-slate-600'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className={`text-sm font-medium ${selectedMember?.dues_suspended ? 'text-red-300' : 'text-slate-300'}`}>
                    {selectedMember?.dues_suspended ? '🚫 Member Suspended' : 'Member Active'}
                  </span>
                  {selectedMember?.dues_suspended && selectedMember?.dues_suspended_at && (
                    <p className="text-xs text-red-400 mt-1">
                      Suspended on {new Date(selectedMember.dues_suspended_at).toLocaleDateString()}
                      {selectedMember?.suspension_reason && ` - ${selectedMember.suspension_reason}`}
                    </p>
                  )}
                </div>
                {selectedMember?.dues_suspended ? (
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={async () => {
                      try {
                        const response = await axios.post(
                          `${BACKEND_URL}/api/members/${selectedMember.id}/unsuspend`,
                          {},
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        // Update local state
                        setMembers(prev => {
                          const updated = { ...prev };
                          const chapter = selectedMember.chapter;
                          updated[chapter] = updated[chapter].map(m => 
                            m.id === selectedMember.id ? { ...m, dues_suspended: false, dues_suspended_at: null } : m
                          );
                          return updated;
                        });
                        setSelectedMember(prev => ({ ...prev, dues_suspended: false, dues_suspended_at: null }));
                        toast.success(response.data.message || 'Member unsuspended');
                        if (response.data.discord_restored) {
                          toast.success('Discord roles restored');
                        }
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to unsuspend member');
                      }
                    }}
                    data-testid="unsuspend-btn"
                  >
                    <Shield className="w-4 h-4 mr-1" />
                    Unsuspend
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="destructive"
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={async () => {
                      if (!window.confirm(`Are you sure you want to suspend ${selectedMember?.handle}? This will remove all their Discord roles.`)) {
                        return;
                      }
                      try {
                        const response = await axios.post(
                          `${BACKEND_URL}/api/members/${selectedMember.id}/suspend`,
                          { reason: 'Manual suspension from A&D page' },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        // Update local state
                        setMembers(prev => {
                          const updated = { ...prev };
                          const chapter = selectedMember.chapter;
                          updated[chapter] = updated[chapter].map(m => 
                            m.id === selectedMember.id ? { ...m, dues_suspended: true, dues_suspended_at: new Date().toISOString() } : m
                          );
                          return updated;
                        });
                        setSelectedMember(prev => ({ ...prev, dues_suspended: true, dues_suspended_at: new Date().toISOString() }));
                        toast.success(response.data.message || 'Member suspended');
                        if (response.data.discord_suspended) {
                          toast.success('Discord roles removed');
                        }
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to suspend member');
                      }
                    }}
                    data-testid="suspend-btn"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Suspend
                  </Button>
                )}
              </div>
            </div>

            {/* Non-Dues Paying Toggle */}
            <div className="p-3 bg-amber-900/30 rounded-lg border border-amber-700">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="exempt-member"
                  checked={selectedMember?.non_dues_paying || false}
                  onCheckedChange={async (checked) => {
                    try {
                      await axios.put(`${BACKEND_URL}/api/members/${selectedMember.id}`, 
                        { non_dues_paying: checked },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      // Update local state
                      setMembers(prev => {
                        const updated = { ...prev };
                        const chapter = selectedMember.chapter;
                        updated[chapter] = updated[chapter].map(m => 
                          m.id === selectedMember.id ? { ...m, non_dues_paying: checked } : m
                        );
                        return updated;
                      });
                      setSelectedMember(prev => ({ ...prev, non_dues_paying: checked }));
                      toast.success(checked ? 'Member marked as exempt from dues' : 'Member is now dues-paying');
                    } catch (error) {
                      toast.error('Failed to update member');
                    }
                  }}
                  data-testid="exempt-member-checkbox"
                />
                <label htmlFor="exempt-member" className="text-sm text-amber-200 cursor-pointer font-medium">
                  Non-Dues Paying Member (Honorary, Exempt)
                </label>
              </div>
              {selectedMember?.non_dues_paying && (
                <p className="text-xs text-amber-300 mt-2 ml-6">
                  This member will not receive dues reminders and is excluded from dues tracking.
                </p>
              )}
            </div>

            {/* Arrangement Toggle */}
            <div className="p-3 bg-green-900/30 rounded-lg border border-green-700">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="arrangement-member"
                  checked={duesForm.hasArrangement || false}
                  onCheckedChange={async (checked) => {
                    try {
                      await axios.put(`${BACKEND_URL}/api/members/${selectedMember.id}`, 
                        { 
                          dues_arrangements_made: checked,
                          dues_arrangements_date: checked ? new Date().toISOString() : null,
                          dues_suspended: checked ? false : selectedMember.dues_suspended
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      // Update local state
                      setMembers(prev => {
                        const updated = { ...prev };
                        const chapter = selectedMember.chapter;
                        updated[chapter] = updated[chapter].map(m => 
                          m.id === selectedMember.id ? { 
                            ...m, 
                            dues_arrangements_made: checked,
                            dues_suspended: checked ? false : m.dues_suspended
                          } : m
                        );
                        return updated;
                      });
                      setSelectedMember(prev => ({ 
                        ...prev, 
                        dues_arrangements_made: checked,
                        dues_suspended: checked ? false : prev.dues_suspended
                      }));
                      setDuesForm(prev => ({ ...prev, hasArrangement: checked }));
                      toast.success(checked ? 'Member marked as having arrangement - will not be suspended' : 'Arrangement status removed');
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to update arrangement status');
                    }
                  }}
                  data-testid="arrangement-member-checkbox"
                />
                <label htmlFor="arrangement-member" className="text-sm text-green-200 cursor-pointer font-medium">
                  Has Payment Arrangement (Will Not Be Suspended)
                </label>
              </div>
              {duesForm.hasArrangement && (
                <p className="text-xs text-green-300 mt-2 ml-6">
                  This member has made payment arrangements and will not receive suspension reminders.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Status</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {DUES_STATUSES.map(status => {
                  const Icon = status.icon;
                  return (
                    <Button
                      key={status.value}
                      type="button"
                      size="sm"
                      className={`${duesForm.status === status.value ? status.color : 'bg-gray-600'} text-xs sm:text-sm px-2 py-2`}
                      onClick={() => setDuesForm({...duesForm, status: status.value})}
                    >
                      <Icon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      <span className="truncate">{status.label}</span>
                    </Button>
                  );
                })}
              </div>
            </div>
            
            {/* Extension Date - shown when Extended is selected */}
            {duesForm.status === 'extended' && (
              <div className="space-y-2 p-3 bg-blue-900/30 rounded-lg border border-blue-700">
                <Label className="text-blue-300 text-sm font-semibold">Extension Date</Label>
                <Input
                  type="date"
                  value={duesForm.extensionDate}
                  onChange={(e) => setDuesForm({...duesForm, extensionDate: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                  className="bg-slate-700 border-slate-600 text-white text-sm"
                  data-testid="extension-date-input"
                />
                <p className="text-xs text-blue-300">
                  Member will not receive dues reminders until this date.
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Notes (Optional)</Label>
              <Textarea 
                value={duesForm.notes}
                onChange={(e) => setDuesForm({...duesForm, notes: e.target.value})}
                placeholder="Any additional notes..."
                className="bg-slate-700 border-slate-600 text-white text-sm min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-4">
            <Button variant="outline" onClick={() => setDuesDialog(false)} className="w-full sm:w-auto border-slate-600 text-slate-300">Cancel</Button>
            <Button 
              variant="outline" 
              onClick={() => {
                setDuesDialog(false);
                openPaymentDialog(selectedMember);
              }} 
              className="w-full sm:w-auto border-green-600 text-green-300 hover:bg-green-900/30"
              data-testid="record-payment-btn"
            >
              <DollarSign className="w-4 h-4 mr-1" />
              Record Payment
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                setDuesDialog(false);
                openBulkCorrectionDialog(selectedMember);
              }} 
              className="w-full sm:w-auto border-amber-600 text-amber-300 hover:bg-amber-900/30"
              data-testid="correct-dues-btn"
            >
              Correct Dues Range
            </Button>
            <Button onClick={handleDuesSubmit} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Dues Correction Dialog */}
      <Dialog open={bulkCorrectionDialog} onOpenChange={setBulkCorrectionDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-[95vw] sm:max-w-lg mx-2 sm:mx-auto p-4 sm:p-6">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-white text-lg">Correct Dues Range</DialogTitle>
            <DialogDescription className="text-sm">
              Set dues status for {selectedMember?.handle} across a date range.
              <span className="block mt-1 text-amber-400">
                Useful for annual dues or correcting mistakes.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">From</Label>
                <div className="flex gap-2">
                  <select
                    value={bulkCorrectionForm.startMonth}
                    onChange={(e) => setBulkCorrectionForm({...bulkCorrectionForm, startMonth: e.target.value})}
                    className="flex-1 p-2 text-sm bg-slate-700 border border-slate-600 rounded text-white"
                  >
                    <option value="">Month</option>
                    {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={bulkCorrectionForm.startYear}
                    onChange={(e) => setBulkCorrectionForm({...bulkCorrectionForm, startYear: e.target.value})}
                    className="w-24 p-2 text-sm bg-slate-700 border border-slate-600 rounded text-white"
                  >
                    {[2024, 2025, 2026, 2027, 2028].map(y => (
                      <option key={y} value={y.toString()}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">To</Label>
                <div className="flex gap-2">
                  <select
                    value={bulkCorrectionForm.endMonth}
                    onChange={(e) => setBulkCorrectionForm({...bulkCorrectionForm, endMonth: e.target.value})}
                    className="flex-1 p-2 text-sm bg-slate-700 border border-slate-600 rounded text-white"
                  >
                    <option value="">Month</option>
                    {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={bulkCorrectionForm.endYear}
                    onChange={(e) => setBulkCorrectionForm({...bulkCorrectionForm, endYear: e.target.value})}
                    className="w-24 p-2 text-sm bg-slate-700 border border-slate-600 rounded text-white"
                  >
                    {[2024, 2025, 2026, 2027, 2028].map(y => (
                      <option key={y} value={y.toString()}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Status Selection */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Set Status To</Label>
              <select
                value={bulkCorrectionForm.status}
                onChange={(e) => setBulkCorrectionForm({...bulkCorrectionForm, status: e.target.value})}
                className="w-full p-2 text-sm bg-slate-700 border border-slate-600 rounded text-white"
              >
                <option value="paid">✅ Paid</option>
                <option value="unpaid">❌ Unpaid</option>
                <option value="late">⚠️ Late</option>
              </select>
            </div>

            {/* Note */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Note (Optional)</Label>
              <Textarea 
                value={bulkCorrectionForm.note}
                onChange={(e) => setBulkCorrectionForm({...bulkCorrectionForm, note: e.target.value})}
                placeholder="e.g., Annual dues paid, Correction for billing error..."
                className="bg-slate-700 border-slate-600 text-white text-sm min-h-[60px]"
              />
            </div>

            {/* Revoke Extension Checkbox */}
            <div className="flex items-center space-x-2 p-3 bg-slate-700/30 rounded-lg border border-slate-600">
              <Checkbox
                id="revoke-extension"
                checked={bulkCorrectionForm.revokeExtension}
                onCheckedChange={(checked) => setBulkCorrectionForm({...bulkCorrectionForm, revokeExtension: checked})}
              />
              <label htmlFor="revoke-extension" className="text-sm text-slate-300 cursor-pointer">
                Also revoke any existing extension
              </label>
            </div>
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-4">
            <Button variant="outline" onClick={() => setBulkCorrectionDialog(false)} className="w-full sm:w-auto border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button 
              onClick={handleBulkCorrectionSubmit} 
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700"
              disabled={!bulkCorrectionForm.startMonth || !bulkCorrectionForm.endMonth}
              data-testid="submit-bulk-correction-btn"
            >
              Apply Correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Recording Dialog */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-[95vw] sm:max-w-md mx-2 sm:mx-auto p-4 sm:p-6">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-white text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-400" />
              Record Dues Payment
            </DialogTitle>
            <DialogDescription className="text-sm">
              Record a payment for {selectedMember?.handle}
              {memberBalance > 0 && (
                <span className="block mt-1 text-green-400">
                  Current credit balance: ${memberBalance.toFixed(2)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Payment Amount */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Payment Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="e.g., 45.00"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white text-lg font-mono"
                data-testid="payment-amount-input"
              />
              {paymentForm.amount && parseFloat(paymentForm.amount) > 0 && (
                <div className="text-xs text-slate-400 p-2 bg-slate-700/50 rounded">
                  {(() => {
                    const total = (memberBalance || 0) + parseFloat(paymentForm.amount || 0);
                    const months = Math.floor(total / 30);
                    const remainder = (total % 30).toFixed(2);
                    return (
                      <>
                        <div className="text-green-400">
                          {months > 0 ? `${months} month(s) will be marked as paid` : 'Credit towards next month'}
                        </div>
                        {remainder > 0 && months > 0 && (
                          <div className="text-blue-400">
                            ${remainder} will be added as credit balance
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Payment Method</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {['cash', 'check', 'square', 'other'].map(method => (
                  <Button
                    key={method}
                    type="button"
                    size="sm"
                    variant="outline"
                    className={`${paymentForm.payment_method === method ? 'bg-green-600 border-green-600 text-white' : 'border-slate-600 text-slate-300'}`}
                    onClick={() => setPaymentForm({...paymentForm, payment_method: method})}
                  >
                    {method.charAt(0).toUpperCase() + method.slice(1)}
                  </Button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Notes (Optional)</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                placeholder="Any additional notes about this payment..."
                className="bg-slate-700 border-slate-600 text-white text-sm min-h-[60px]"
              />
            </div>
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-4">
            <Button variant="outline" onClick={() => setPaymentDialog(false)} className="w-full sm:w-auto border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button 
              onClick={handlePaymentSubmit} 
              className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
              disabled={paymentLoading || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0}
              data-testid="submit-payment-btn"
            >
              {paymentLoading ? 'Processing...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Member Meetings Dialog */}
      <Dialog open={viewMeetingsDialog} onOpenChange={setViewMeetingsDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-slate-800 border-slate-700 mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Meeting Attendance History</DialogTitle>
            <DialogDescription>
              Meetings attended by {viewMeetingsMember?.handle}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4" ref={printRef}>
            {viewMeetingsMember && (
              <>
                {/* Header with stats */}
                <div className="flex items-center justify-between bg-slate-800 p-3 rounded-lg">
                  <div>
                    <span className="font-bold text-lg">{viewMeetingsMember.handle}</span>
                    <span className="text-muted-foreground ml-2">({viewMeetingsMember.title || 'Member'})</span>
                  </div>
                  <div className="text-right">
                    {(() => {
                      const stats = getAttendanceStats(viewMeetingsMember.id);
                      return (
                        <div>
                          <span className={stats.rate >= 80 ? 'text-green-500' : stats.rate >= 50 ? 'text-yellow-500' : 'text-red-500'}>
                            {stats.rate}% Attendance
                          </span>
                          <span className="text-muted-foreground text-sm ml-2">
                            ({stats.present}/{stats.total} meetings)
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Filter and Print Controls */}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Label>Filter by Month:</Label>
                    <Select value={viewMeetingsFilter} onValueChange={setViewMeetingsFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Time</SelectItem>
                        {getAvailableMonths(viewMeetingsMember.id).map(month => (
                          <SelectItem key={month} value={month}>{formatMonth(month)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" onClick={handlePrintMeetings}>
                    <Printer className="w-4 h-4 mr-2" />
                    Print
                  </Button>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Meeting Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Notes</TableHead>
                      {canEdit && <TableHead>Delete</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getFilteredMeetings(viewMeetingsMember.id)
                      .map((record, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{record.meeting_date}</TableCell>
                          <TableCell>{getMeetingTypeLabel(record.meeting_type)}</TableCell>
                          <TableCell>{getStatusBadge(record.status)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{record.notes || '-'}</TableCell>
                          {canEdit && (
                            <TableCell>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleDeleteAttendance(record.id)}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    }
                    {getFilteredMeetings(viewMeetingsMember?.id || '').length === 0 && (
                      <TableRow>
                        <TableCell colSpan={canEdit ? 5 : 4} className="text-center text-muted-foreground py-8">
                          {viewMeetingsFilter === 'all' ? 'No meeting records found' : `No meetings found for ${formatMonth(viewMeetingsFilter)}`}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setViewMeetingsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dues History Dialog */}
      <Dialog open={duesHistoryDialog} onOpenChange={setDuesHistoryDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-slate-800 border-slate-700 mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Dues Payment History</DialogTitle>
            <DialogDescription>
              Payment history for {duesHistoryMember?.handle}
            </DialogDescription>
          </DialogHeader>
          
          {loadingDuesHistory ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-muted-foreground">Loading history...</span>
            </div>
          ) : duesHistoryData ? (
            <div className="space-y-6">
              {/* Credit Balance */}
              {duesHistoryData.balance > 0 && (
                <div className="bg-green-900/30 rounded-lg p-4 border border-green-700/50">
                  <h3 className="font-semibold text-green-300 mb-1">Credit Balance</h3>
                  <div className="text-2xl font-bold text-green-400">${duesHistoryData.balance.toFixed(2)}</div>
                  <p className="text-xs text-green-400/70 mt-1">This credit will be applied to the next unpaid month</p>
                </div>
              )}

              {/* Last Paid Summary */}
              <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                <h3 className="font-semibold text-white mb-2">Last Payment</h3>
                {duesHistoryData.last_paid ? (
                  <div className="text-lg">
                    <span className="text-green-400 font-bold">{duesHistoryData.last_paid}</span>
                    {duesHistoryData.last_paid_note && (
                      <span className="text-sm text-slate-400 ml-2">- {duesHistoryData.last_paid_note}</span>
                    )}
                  </div>
                ) : (
                  <span className="text-slate-400">No payment records found</span>
                )}
              </div>

              {/* Subscription Info */}
              {duesHistoryData.subscription_info && (
                <div className="bg-purple-900/30 rounded-lg p-4 border border-purple-700/50">
                  <h3 className="font-semibold text-purple-300 mb-2">Square Subscription</h3>
                  <div className="text-sm space-y-1">
                    <div><span className="text-slate-400">Customer:</span> <span className="text-white">{duesHistoryData.subscription_info.customer_name}</span></div>
                    <div><span className="text-slate-400">Last Synced:</span> <span className="text-white">{duesHistoryData.subscription_info.last_synced ? new Date(duesHistoryData.subscription_info.last_synced).toLocaleString() : 'Never'}</span></div>
                    <div><span className="text-slate-400">Subscription ID:</span> <span className="text-xs text-slate-300 font-mono">{duesHistoryData.subscription_info.square_subscription_id}</span></div>
                  </div>
                </div>
              )}

              {/* Square Payments */}
              {duesHistoryData.square_payments?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-white mb-3">Square Payment History</h3>
                  <div className="space-y-2">
                    {duesHistoryData.square_payments.map((payment, idx) => (
                      <div key={idx} className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="text-green-400 font-bold">${payment.amount?.toFixed(2) || '0.00'} {payment.currency || 'USD'}</div>
                            {payment.paid_at ? (
                              <div className="text-xs text-slate-400">
                                <span className="text-slate-500">Paid:</span> {new Date(payment.paid_at).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            ) : payment.invoice_date && (
                              <div className="text-xs text-slate-400">
                                <span className="text-slate-500">Invoice Date:</span> {new Date(payment.invoice_date).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric'
                                })}
                              </div>
                            )}
                          </div>
                          <Badge className={
                            payment.status === 'PAID' || payment.status === 'COMPLETED' || payment.status === 'PAID_OTP'
                              ? (payment.status === 'PAID_OTP' ? 'bg-cyan-600' : 'bg-green-600')
                              : 'bg-yellow-600'
                          }>
                            {payment.status === 'PAID_OTP' ? 'PAID (one-time)' : payment.status}
                          </Badge>
                        </div>
                        <div className="mt-2 text-xs space-y-1">
                          {payment.payment_id && (
                            <div>
                              <span className="text-slate-400">Transaction ID:</span>
                              <span className="text-slate-300 font-mono ml-1 break-all">{payment.payment_id}</span>
                            </div>
                          )}
                          {payment.invoice_id && (
                            <div>
                              <span className="text-slate-400">Invoice ID:</span>
                              <span className="text-slate-300 font-mono ml-1 break-all">{payment.invoice_id}</span>
                            </div>
                          )}
                        </div>
                        {payment.receipt_url && (
                          <a 
                            href={payment.receipt_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-400 hover:underline mt-1 inline-block"
                          >
                            View Receipt →
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Dues Records */}
              {duesHistoryData.dues_records?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-white mb-3">Manual Dues Records</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {duesHistoryData.dues_records.map((record, idx) => (
                      <div key={idx} className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 flex justify-between items-center">
                        <div>
                          <span className="text-white">{record.month}</span>
                          {record.notes && <span className="text-xs text-slate-400 ml-2">- {record.notes}</span>}
                        </div>
                        {getStatusBadge(record.status)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Data Message */}
              {!duesHistoryData.square_payments?.length && !duesHistoryData.dues_records?.length && !duesHistoryData.last_paid && (
                <div className="text-center py-8 text-slate-400">
                  <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No payment history found for this member.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <p>Failed to load dues history.</p>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setDuesHistoryDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default OfficerTracking;
